import React, { useState } from 'react';
import axios from 'axios';
import { useLang } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { LifeBuoy, Upload, CheckCircle2, AlertTriangle, KeyRound, Lock, Link2, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QRIS_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_89a1b5b9-2080-44e2-9f5e-2d062c3e298d/artifacts/1ff73lgr_image.png';

// Accepts http(s) URLs only. Requires a host with a dot (rejects plain text like "tiktok.com/foo" without protocol).
function isValidURL(s) {
  if (!s || typeof s !== 'string') return false;
  const trimmed = s.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    const u = new URL(trimmed);
    return !!u.hostname && u.hostname.includes('.');
  } catch {
    return false;
  }
}

// WhatsApp: must start with + and contain 8-15 digits total.
function isValidWA(s) {
  if (!s || typeof s !== 'string') return false;
  const trimmed = s.trim().replace(/[\s-]/g, '');
  return /^\+\d{8,15}$/.test(trimmed);
}

/**
 * Public ticketing / support form. Rendered at the bottom of the landing page,
 * before the footer. Two modes:
 *  - delete_post: user posts the post link + reason
 *  - delete_drive: user gives member code (P001/L001) + full matching data
 *                  (required to prove ownership); if reason is personal-basis,
 *                  charges Rp 48,000 and requires payment-proof upload.
 */
export default function TicketSupport() {
  const { lang } = useLang();
  const [expanded, setExpanded] = useState(false);

  // Ticket unlock is SEPARATE from the global auth token — it lives only in
  // sessionStorage so it clears when the tab is closed and never inherits
  // from a normal member login. Every visit MUST re-enter the passkey.
  const [ticketUnlocked, setTicketUnlocked] = useState(() => {
    try { return sessionStorage.getItem('mg_ticket_unlocked') === '1'; }
    catch { return false; }
  });
  const [unlockedWA, setUnlockedWA] = useState(() => {
    try { return sessionStorage.getItem('mg_ticket_wa') || ''; }
    catch { return ''; }
  });

  // Inline passkey unlock form state
  const [unlockWA, setUnlockWA] = useState('');
  const [unlockPasskey, setUnlockPasskey] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);

  const [f, setF] = useState({
    ticket_type: 'delete_post',
    reporter_name: '', reporter_whatsapp: '',
    reason: '', reason_basis: 'policy',
    post_link: '',
    member_code: '',
    match_full_name: '', match_whatsapp: '', match_domicile: '',
    match_phone: '', match_education: '', match_occupation: '',
    offender_name: '', offender_code: '', report_category: 'harassment', truth_declared: false,
    male_full_name: '', male_age: '', male_domicile: '', male_member_code: '',
    female_full_name: '', female_age: '', female_domicile: '', female_member_code: '',
    match_confirmed: false,
  });
  const [proofFile, setProofFile] = useState(null);
  const [marriageCertFile, setMarriageCertFile] = useState(null);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const needsPayment = f.ticket_type === 'delete_drive' && f.reason_basis === 'personal';
  const needsMarriageCert = f.ticket_type === 'delete_drive' && f.reason_basis === 'policy';

  const unlockTicket = async () => {
    if (!unlockWA.trim() || !unlockPasskey.trim()) {
      return toast.error(lang === 'en' ? 'Fill WhatsApp & Passkey' : 'Isi WhatsApp & Passkey');
    }
    setUnlockLoading(true);
    try {
      // We hit the login endpoint purely to verify the passkey. We do NOT
      // persist the token into AuthContext — the returned token is discarded
      // so this unlock never leaks into a member login session.
      await axios.post(`${API}/auth/login`, {
        whatsapp: unlockWA.trim(),
        passkey: unlockPasskey.trim(),
      });
      const wa = unlockWA.trim();
      setTicketUnlocked(true);
      setUnlockedWA(wa);
      try {
        sessionStorage.setItem('mg_ticket_unlocked', '1');
        sessionStorage.setItem('mg_ticket_wa', wa);
      } catch { /* ignore storage errors */ }
      setF(prev => ({ ...prev, reporter_whatsapp: wa }));
      setUnlockPasskey('');
      toast.success(lang === 'en' ? 'Unlocked. Please fill your ticket.' : 'Berhasil. Silakan isi tiket Anda.');
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'en' ? 'Invalid passkey' : 'Passkey salah'));
    }
    setUnlockLoading(false);
  };

  const lockTicket = () => {
    setTicketUnlocked(false);
    setExpanded(false);
    setUnlockedWA('');
    try {
      sessionStorage.removeItem('mg_ticket_unlocked');
      sessionStorage.removeItem('mg_ticket_wa');
    } catch { /* ignore storage errors */ }
  };

  const submit = async () => {
    // ---- Common required fields ----
    const missing = [];
    if (!f.reporter_name.trim()) missing.push(lang === 'en' ? 'Your full name' : 'Nama lengkap Anda');
    if (!f.reporter_whatsapp.trim()) {
      missing.push(lang === 'en' ? 'Your WhatsApp' : 'WhatsApp Anda');
    } else if (!isValidWA(f.reporter_whatsapp)) {
      return toast.error(lang === 'en'
        ? 'WhatsApp must be in international format, e.g. +6281234567890'
        : 'WhatsApp harus format internasional, mis. +6281234567890');
    }

    // ---- Per-ticket-type required fields ----
    if (f.ticket_type === 'delete_post') {
      if (!f.post_link.trim()) {
        missing.push(lang === 'en' ? 'Post link (URL)' : 'Link postingan (URL)');
      } else if (!isValidURL(f.post_link)) {
        return toast.error(lang === 'en'
          ? 'Post link must be a valid URL starting with http:// or https:// (e.g. https://tiktok.com/@user/video/...)'
          : 'Link postingan harus URL lengkap diawali http:// atau https:// (mis. https://tiktok.com/@user/video/...)');
      }
      if (!f.reason.trim()) missing.push(lang === 'en' ? 'Reason for removal' : 'Alasan penghapusan');
    }

    if (f.ticket_type === 'delete_drive') {
      if (!f.member_code.trim()) missing.push(lang === 'en' ? 'Member code (P001 / L001)' : 'Kode Member (P001 / L001)');
      if (!f.match_full_name.trim()) missing.push(lang === 'en' ? 'Full name' : 'Nama lengkap');
      if (!f.match_whatsapp.trim()) {
        missing.push(lang === 'en' ? 'WhatsApp (matches registration)' : 'WA (sesuai pendaftaran)');
      } else if (!isValidWA(f.match_whatsapp)) {
        return toast.error(lang === 'en'
          ? 'Matched WhatsApp must be in international format, e.g. +6281234567890'
          : 'WA yang dicocokkan harus format internasional, mis. +6281234567890');
      }
      if (!f.match_domicile.trim()) missing.push(lang === 'en' ? 'Domicile (city)' : 'Domisili (kota)');
      if (!f.match_phone.trim()) missing.push(lang === 'en' ? 'HP number' : 'No. HP');
      if (!f.match_education.trim()) missing.push(lang === 'en' ? 'Education' : 'Pendidikan');
      if (!f.match_occupation.trim()) missing.push(lang === 'en' ? 'Occupation' : 'Pekerjaan');
      if (!f.reason.trim()) missing.push(lang === 'en' ? 'Reason detail' : 'Detail alasan');
    }

    if (f.ticket_type === 'match_report') {
      if (!f.male_full_name.trim()) missing.push(lang === 'en' ? 'Male full name' : 'Nama lengkap pria');
      if (!f.male_age.trim() || !/^\d+$/.test(String(f.male_age).trim())) missing.push(lang === 'en' ? 'Male age (number)' : 'Usia pria (angka)');
      if (!f.male_domicile.trim()) missing.push(lang === 'en' ? 'Male domicile' : 'Domisili pria');
      if (!f.male_member_code.trim()) missing.push(lang === 'en' ? 'Male member code (L001)' : 'Kode member pria (L001)');
      if (!f.female_full_name.trim()) missing.push(lang === 'en' ? 'Female full name' : 'Nama lengkap wanita');
      if (!f.female_age.trim() || !/^\d+$/.test(String(f.female_age).trim())) missing.push(lang === 'en' ? 'Female age (number)' : 'Usia wanita (angka)');
      if (!f.female_domicile.trim()) missing.push(lang === 'en' ? 'Female domicile' : 'Domisili wanita');
      if (!f.female_member_code.trim()) missing.push(lang === 'en' ? 'Female member code (P001)' : 'Kode member wanita (P001)');
      if (!f.match_confirmed) {
        return toast.error(lang === 'en' ? 'You must confirm the match agreement' : 'Anda wajib centang pernyataan konfirmasi match');
      }
    }

    if (f.ticket_type === 'report') {
      if (!f.offender_name.trim()) missing.push(lang === 'en' ? 'Offender full name' : 'Nama lengkap oknum');
      if (!f.offender_code.trim()) missing.push(lang === 'en' ? 'Offender code (L001 / P001)' : 'Kode oknum (L001 / P001)');
      if (!f.reason.trim()) missing.push(lang === 'en' ? 'Explanation of what happened' : 'Penjelasan kejadian');
    }

    if (missing.length > 0) {
      return toast.error(
        (lang === 'en' ? 'Please complete these fields first: ' : 'Lengkapi kolom berikut dulu: ') + missing.join(', '),
        { duration: 6000 }
      );
    }

    // ---- File / checkbox gates (order after field checks so user sees them in flow) ----
    if (needsPayment && !proofFile) {
      return toast.error(lang === 'en' ? 'Upload payment proof required' : 'Bukti pembayaran wajib diunggah');
    }
    if (needsMarriageCert && !marriageCertFile) {
      return toast.error(lang === 'en' ? 'Marriage certificate proof required' : 'Wajib upload bukti akta nikah / surat pemberkatan');
    }
    if (f.ticket_type === 'report') {
      if (!f.truth_declared) {
        return toast.error(lang === 'en' ? 'You must confirm the report is true' : 'Anda wajib centang pernyataan bahwa laporan ini benar');
      }
      if (f.report_category === 'harassment' && evidenceFiles.length === 0) {
        return toast.error(lang === 'en' ? 'Please upload at least one evidence file for harassment reports' : 'Wajib upload minimal 1 bukti untuk laporan pelecehan');
      }
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tickets/create`, f);
      const ticketId = data.ticket.id;
      if (proofFile) {
        const fd = new FormData(); fd.append('file', proofFile);
        await axios.post(`${API}/tickets/${ticketId}/payment-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      if (marriageCertFile) {
        const fd = new FormData(); fd.append('file', marriageCertFile);
        await axios.post(`${API}/tickets/${ticketId}/marriage-cert`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      for (const ef of evidenceFiles) {
        const fd = new FormData(); fd.append('file', ef);
        await axios.post(`${API}/tickets/${ticketId}/evidence`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(lang === 'en' ? 'Ticket submitted!' : 'Tiket dikirim!');
      setSubmitted(ticketId);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'en' ? 'Failed' : 'Gagal'));
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <section id="ticket-support" className="py-14 sm:py-20 px-4 sm:px-6" style={{ borderTop: '1px solid var(--border-color)' }}>
        <div className="max-w-2xl mx-auto text-center" data-testid="ticket-success">
          <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold t-text font-romantic mb-2">
            {lang === 'en' ? 'Ticket Submitted' : 'Tiket Terkirim'}
          </h2>
          <p className="t-text-secondary text-sm mb-4">
            {lang === 'en'
              ? 'Your ticket ID is below. Please save it and our admin will contact you via WhatsApp.'
              : 'ID tiket Anda di bawah. Simpan baik-baik, admin akan meninjau dan menghubungi Anda via fitur chat di website ini.'}
          </p>
          <code className="px-4 py-2 rounded-lg glass-card text-sm font-mono t-text">{submitted}</code>
          <div>
            <Button onClick={() => { setSubmitted(null); setProofFile(null); setMarriageCertFile(null); setEvidenceFiles([]); setF(prev => ({ ...prev, reason: '', post_link: '', member_code: '', match_full_name: '', match_whatsapp: '', match_domicile: '', match_phone: '', match_education: '', match_occupation: '' })); }}
              className="mt-6 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="ticket-reset-btn">
              {lang === 'en' ? 'Submit Another' : 'Kirim Tiket Lain'}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="ticket-support" className="py-14 sm:py-20 px-4 sm:px-6" style={{ borderTop: '1px solid var(--border-color)' }}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-3">
            <LifeBuoy className="w-4 h-4 text-[#D98A94]" />
            <span className="text-xs t-text-secondary font-medium tracking-wide">
              {lang === 'en' ? 'Support & Complaint Tickets' : 'Sistem Tiket Dukungan & Komplain'}
            </span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold t-text font-romantic tracking-tight">
            {lang === 'en' ? 'Ticket System' : 'Sistem Tiketing'}
          </h2>
          <p className="t-text-secondary text-sm mt-3 max-w-xl mx-auto">
            {lang === 'en'
              ? 'Request post removal, data deletion, or send other complaints. Every ticket is reviewed by admin.'
              : 'Ajukan hapus postingan, hapus data, atau kirim komplain lainnya. Setiap tiket direview admin.'}
          </p>

          {/* Shareable link + track link */}
          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            <button
              onClick={async () => {
                const url = `${window.location.origin}/ticket`;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success(lang === 'en' ? 'Direct link copied!' : 'Link tiket disalin!');
                } catch {
                  toast.error(lang === 'en' ? 'Copy failed' : 'Gagal menyalin');
                }
              }}
              data-testid="ticket-copy-share-link"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold t-text-secondary hover:text-[#D98A94] bg-white/60 border rounded-full px-3 py-1.5"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <Link2 className="w-3 h-3" />
              {lang === 'en' ? 'Copy Ticket Link' : 'Salin Link Tiket'}
            </button>
            <a
              href="/ticket"
              data-testid="ticket-go-track"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#D98A94] hover:underline bg-white/60 border rounded-full px-3 py-1.5"
              style={{ borderColor: 'rgba(217,138,148,0.35)' }}
            >
              <ClipboardList className="w-3 h-3" />
              {lang === 'en' ? 'Track My Ticket' : 'Cek Status Tiket'}
            </a>
          </div>
        </div>

        {!ticketUnlocked ? (
          <div className="glass-card rounded-3xl p-6 sm:p-8" data-testid="ticket-passkey-gate">
            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3" style={{ background: 'rgba(217,138,148,0.12)' }}>
                <Lock className="w-6 h-6 text-[#D98A94]" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold t-text mb-1">
                {lang === 'en' ? 'Passkey required to open ticket form' : 'Passkey wajib untuk membuka form tiket'}
              </h3>
              <p className="t-text-secondary text-xs sm:text-sm max-w-md mx-auto">
                {lang === 'en'
                  ? 'Enter your WhatsApp number and the monthly passkey given by admin. Without a valid passkey, the ticket form stays locked.'
                  : 'Masukkan nomor WhatsApp dan passkey bulanan yang diberikan admin. Tanpa passkey yang valid, form tiket tetap terkunci.'}
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-3">
              <Input
                placeholder={lang === 'en' ? 'WhatsApp (e.g. +6281234567890)' : 'WhatsApp (mis. +6281234567890)'}
                value={unlockWA}
                onChange={e => setUnlockWA(e.target.value)}
                data-testid="ticket-unlock-wa"
                autoComplete="off"
              />
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 t-text-muted" />
                <Input
                  type="password"
                  placeholder={lang === 'en' ? 'Passkey' : 'Kode Passkey'}
                  value={unlockPasskey}
                  onChange={e => setUnlockPasskey(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') unlockTicket(); }}
                  className="pl-9"
                  data-testid="ticket-unlock-passkey"
                  autoComplete="off"
                />
              </div>
              <Button
                onClick={unlockTicket}
                disabled={unlockLoading}
                data-testid="ticket-unlock-btn"
                className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full h-11 font-semibold"
              >
                {unlockLoading
                  ? (lang === 'en' ? 'Verifying...' : 'Memverifikasi...')
                  : (lang === 'en' ? 'Unlock Ticket Form' : 'Buka Form Tiket')}
              </Button>
              <p className="text-[11px] t-text-muted text-center pt-1">
                {lang === 'en'
                  ? 'Passkey is provided monthly by admin via WhatsApp.'
                  : 'Passkey diberikan admin setiap bulan via WhatsApp.'}
              </p>
            </div>
          </div>
        ) : !expanded ? (
          <div className="glass-card rounded-3xl p-8 text-center" data-testid="ticket-collapsed">
            <LifeBuoy className="w-10 h-10 text-[#D98A94] mx-auto mb-3" />
            <p className="text-xs t-text-muted mb-1">
              {lang === 'en' ? 'Unlocked as' : 'Terbuka untuk'} <span className="font-semibold">{unlockedWA}</span>
            </p>
            <p className="t-text-secondary text-sm mb-4">
              {lang === 'en' ? 'Click below to open a new support ticket.' : 'Klik untuk membuka tiket baru.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              <Button onClick={() => setExpanded(true)} data-testid="ticket-open-form-btn"
                className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full px-6">
                {lang === 'en' ? 'Open New Ticket' : 'Buka Tiket Baru'}
              </Button>
              <Button onClick={lockTicket} variant="ghost" data-testid="ticket-relock-btn"
                className="rounded-full text-xs t-text-muted">
                <Lock className="w-3 h-3 mr-1" />
                {lang === 'en' ? 'Lock Again' : 'Kunci Lagi'}
              </Button>
            </div>
          </div>
        ) : (
        <div className="glass-card rounded-3xl p-5 sm:p-8 space-y-4">
          {/* Ticket type tabs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { k: 'delete_post', l: lang === 'en' ? 'Delete Post' : 'Hapus Post' },
              { k: 'delete_drive', l: lang === 'en' ? 'Delete Drive' : 'Hapus Drive' },
              { k: 'match_report', l: lang === 'en' ? 'Report Match' : 'Lapor Match' },
              { k: 'report', l: lang === 'en' ? 'Report Misconduct' : 'Laporkan Masalah' },
            ].map(o => (
              <button key={o.k} onClick={() => set('ticket_type', o.k)} data-testid={`ticket-type-${o.k}`}
                className={`py-2.5 rounded-lg text-xs sm:text-sm font-medium ${f.ticket_type === o.k ? 'bg-[#D98A94] text-white' : 'glass-card t-text-secondary'}`}>{o.l}</button>
            ))}
          </div>

          {/* Reporter info */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Input placeholder={lang === 'en' ? 'Your Full Name *' : 'Nama Lengkap Anda *'} value={f.reporter_name} onChange={e => set('reporter_name', e.target.value)} data-testid="ticket-reporter-name" />
            <div>
              <Input placeholder="WhatsApp * (+6281…)" value={f.reporter_whatsapp} onChange={e => set('reporter_whatsapp', e.target.value)} inputMode="tel" data-testid="ticket-reporter-wa"
                className={f.reporter_whatsapp && !isValidWA(f.reporter_whatsapp) ? 'border-red-400 focus:border-red-500' : ''} />
              {f.reporter_whatsapp && !isValidWA(f.reporter_whatsapp) && (
                <p className="text-[11px] text-red-600 mt-1">
                  {lang === 'en' ? 'Format: +6281234567890 (with country code)' : 'Format: +6281234567890 (dengan kode negara)'}
                </p>
              )}
            </div>
          </div>

          {f.ticket_type === 'delete_post' && (
            <>
              <div>
                <Input placeholder={lang === 'en' ? 'Post Link (https://...) *' : 'Link Postingan (https://...) *'} value={f.post_link}
                  onChange={e => set('post_link', e.target.value)} type="url" inputMode="url" data-testid="ticket-post-link"
                  className={f.post_link && !isValidURL(f.post_link) ? 'border-red-400 focus:border-red-500' : ''} />
                {f.post_link && !isValidURL(f.post_link) && (
                  <p className="text-[11px] text-red-600 mt-1">
                    {lang === 'en'
                      ? 'Must be a full URL starting with http:// or https:// (e.g. https://tiktok.com/@user/video/123)'
                      : 'Harus URL lengkap diawali http:// atau https:// (mis. https://tiktok.com/@user/video/123)'}
                  </p>
                )}
                {!f.post_link && (
                  <p className="text-[11px] t-text-muted mt-1">
                    {lang === 'en'
                      ? 'Paste the actual link to the post — plain text is not accepted.'
                      : 'Salin link langsung ke postingan — teks biasa tidak diterima.'}
                  </p>
                )}
              </div>
              <Textarea placeholder={lang === 'en' ? 'Reason for removal *' : 'Alasan penghapusan *'} value={f.reason} onChange={e => set('reason', e.target.value)} data-testid="ticket-reason" />
            </>
          )}

          {f.ticket_type === 'delete_drive' && (
            <>
              <div className="rounded-lg p-3 text-xs t-text-secondary" style={{ background: 'rgba(217,138,148,0.08)', border: '1px solid rgba(217,138,148,0.25)' }}>
                <b className="t-text">{lang === 'en' ? 'Verification required.' : 'Verifikasi wajib.'}</b>{' '}
                {lang === 'en' ? 'To delete drive data, provide member code (e.g. P001 for female / L001 for male) AND matching registration details. Without these we cannot process.' : 'Untuk hapus data di drive, isi kode member (mis. P001 untuk perempuan / L001 untuk laki-laki) DAN data pendaftaran yang cocok. Tanpa ini tidak bisa diproses.'}
              </div>
              <Input placeholder={lang === 'en' ? 'Member Code (P001 / L001) *' : 'Kode Member (P001 / L001) *'} value={f.member_code} onChange={e => set('member_code', e.target.value.toUpperCase())} data-testid="ticket-member-code" />
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder={lang === 'en' ? 'Full Name (matches registration) *' : 'Nama Lengkap (sesuai pendaftaran) *'} value={f.match_full_name} onChange={e => set('match_full_name', e.target.value)} data-testid="ticket-match-name" />
                <Input placeholder={lang === 'en' ? 'WhatsApp (matches registration) *' : 'WA (sesuai pendaftaran) *'} value={f.match_whatsapp} onChange={e => set('match_whatsapp', e.target.value)} data-testid="ticket-match-wa" />
                <Input placeholder={lang === 'en' ? 'Domicile (city) *' : 'Domisili (kota) *'} value={f.match_domicile} onChange={e => set('match_domicile', e.target.value)} data-testid="ticket-match-domicile" />
                <Input placeholder={lang === 'en' ? 'HP number *' : 'No. HP *'} value={f.match_phone} onChange={e => set('match_phone', e.target.value)} data-testid="ticket-match-phone" />
                <Input placeholder={lang === 'en' ? 'Education *' : 'Pendidikan *'} value={f.match_education} onChange={e => set('match_education', e.target.value)} data-testid="ticket-match-edu" />
                <Input placeholder={lang === 'en' ? 'Occupation *' : 'Pekerjaan *'} value={f.match_occupation} onChange={e => set('match_occupation', e.target.value)} data-testid="ticket-match-occ" />
              </div>

              {/* Reason basis */}
              <div>
                <label className="text-xs t-text-muted uppercase tracking-wide mb-2 block">{lang === 'en' ? 'Reason Basis' : 'Dasar Permintaan'}</label>
                <div className="grid sm:grid-cols-2 gap-2">
                  <button onClick={() => set('reason_basis', 'policy')} data-testid="ticket-basis-policy"
                    className={`p-3 rounded-lg text-left text-sm ${f.reason_basis === 'policy' ? 'bg-[#D98A94] text-white' : 'glass-card t-text-secondary'}`}>
                    <div className="font-semibold text-xs">{lang === 'en' ? 'Married (free)' : 'Sudah Menikah (gratis)'}</div>
                    <div className="text-[10px] opacity-80 mt-1">{lang === 'en' ? 'Attach marriage certificate' : 'Sertakan bukti akta nikah'}</div>
                  </button>
                  <button onClick={() => set('reason_basis', 'personal')} data-testid="ticket-basis-personal"
                    className={`p-3 rounded-lg text-left text-sm ${f.reason_basis === 'personal' ? 'bg-[#D98A94] text-white' : 'glass-card t-text-secondary'}`}>
                    <div className="font-semibold text-xs">{lang === 'en' ? 'Personal (Rp 48,000)' : 'Alasan Pribadi (Rp 48.000)'}</div>
                    <div className="text-[10px] opacity-80 mt-1">{lang === 'en' ? 'Fee applies + payment proof' : 'Dikenakan biaya + upload bukti bayar'}</div>
                  </button>
                </div>
              </div>

              <Textarea placeholder={lang === 'en' ? 'Reason detail' : 'Detail alasan'} value={f.reason} onChange={e => set('reason', e.target.value)} data-testid="ticket-reason" />

              {needsMarriageCert && (
                <div className="rounded-lg p-4 space-y-3" style={{ background: 'rgba(217,138,148,0.06)', border: '1px solid rgba(217,138,148,0.30)' }}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#D98A94] flex-shrink-0 mt-0.5" />
                    <div className="text-xs t-text-secondary">
                      <b className="t-text">{lang === 'en' ? 'Marriage proof required (free basis).' : 'Bukti resmi menikah wajib (dasar gratis).'}</b>{' '}
                      {lang === 'en'
                        ? 'Upload marriage certificate (akta nikah), church blessing letter (surat pemberkatan), or any similar official document proving the marriage. Image or PDF, max 8 MB.'
                        : 'Upload akta nikah, surat pemberkatan gereja, atau dokumen resmi sejenis yang menyatakan resmi menikah. Gambar atau PDF, maksimal 8 MB.'}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs t-text-muted uppercase tracking-wide">
                      {lang === 'en' ? 'Marriage Certificate *' : 'Bukti Resmi Menikah *'}
                    </label>
                    <input type="file" accept="image/*,application/pdf" onChange={e => setMarriageCertFile(e.target.files?.[0] || null)}
                      data-testid="ticket-marriage-cert-input"
                      className="mt-1 block text-sm t-text-secondary" />
                    {marriageCertFile && (
                      <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                        <Upload className="w-3 h-3" />{marriageCertFile.name}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {needsPayment && (
                <div className="rounded-lg p-4 space-y-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.30)' }}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs t-text-secondary">
                      <b className="t-text">{lang === 'en' ? 'Personal-basis fee: Rp 48,000.' : 'Biaya alasan pribadi: Rp 48.000.'}</b>{' '}
                      {lang === 'en' ? 'This fee minimizes prank requests and covers processing time.' : 'Biaya ini untuk meminimalkan permintaan iseng dan menutup waktu proses.'}
                    </div>
                  </div>
                  <div className="text-center">
                    <img src={QRIS_IMG} alt="QRIS Match Group" className="w-48 mx-auto rounded-lg" data-testid="qris-image" />
                    <p className="text-[11px] t-text-muted mt-1">{lang === 'en' ? 'Scan to pay via QRIS' : 'Scan untuk bayar via QRIS'}</p>
                  </div>
                  <div>
                    <label className="text-xs t-text-muted uppercase tracking-wide">{lang === 'en' ? 'Payment Proof *' : 'Bukti Pembayaran *'}</label>
                    <input type="file" accept="image/*" onChange={e => setProofFile(e.target.files?.[0] || null)} data-testid="ticket-proof-input"
                      className="mt-1 block text-sm t-text-secondary" />
                    {proofFile && <span className="text-xs text-green-600 flex items-center gap-1 mt-1"><Upload className="w-3 h-3" />{proofFile.name}</span>}
                  </div>
                </div>
              )}
            </>
          )}

          {f.ticket_type === 'match_report' && (
            <>
              <div className="rounded-lg p-3 text-xs t-text-secondary" style={{ background: 'rgba(217,138,148,0.10)', border: '1px solid rgba(217,138,148,0.35)' }}>
                <b className="t-text">{lang === 'en' ? 'Report a successful match.' : 'Laporkan pasangan yang berhasil match / jadian.'}</b>{' '}
                {lang === 'en'
                  ? 'Please fill in both sides. After confirmation, both records will be removed from Our catalog and a MATCH stamp will be added to the profile card on our website. Free of charge.'
                  : 'Silakan isi data kedua belah pihak. Setelah konfirmasi, kedua data anggota akan dihapus dari katalog/drive dan akan distamp MATCH pada kartu profil di website. Semua proses ini GRATIS.'}
                <div className="mt-2 text-[11px] italic t-text-muted">
                  {lang === 'en'
                    ? 'Note: Once married, please submit another ticket and attach the marriage certificate to have the profile removed from the website — also free.'
                    : 'Catatan: Jika sudah menikah, silakan ajukan tiket lagi (jenis "Hapus Drive") dengan upload bukti akta nikah untuk dihapus dari website — juga GRATIS.'}
                </div>
              </div>

              {/* Male side */}
              <div className="rounded-lg p-3 space-y-2" style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)' }}>
                <div className="text-xs font-bold uppercase tracking-wide text-blue-600">
                  ♂ {lang === 'en' ? 'Male' : 'Pihak Pria'}
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input placeholder={lang === 'en' ? 'Full Name *' : 'Nama Lengkap *'} value={f.male_full_name} onChange={e => set('male_full_name', e.target.value)} data-testid="ticket-match-male-name" />
                  <Input placeholder={lang === 'en' ? 'Age *' : 'Usia *'} value={f.male_age} onChange={e => set('male_age', e.target.value)} data-testid="ticket-match-male-age" />
                  <Input placeholder={lang === 'en' ? 'Domicile (city) *' : 'Domisili (kota) *'} value={f.male_domicile} onChange={e => set('male_domicile', e.target.value)} data-testid="ticket-match-male-domicile" />
                  <Input placeholder={lang === 'en' ? 'Member Code (e.g. L001) *' : 'Nomor Member (mis. L001) *'} value={f.male_member_code} onChange={e => set('male_member_code', e.target.value.toUpperCase())} data-testid="ticket-match-male-code" />
                </div>
              </div>

              {/* Female side */}
              <div className="rounded-lg p-3 space-y-2" style={{ background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.25)' }}>
                <div className="text-xs font-bold uppercase tracking-wide text-pink-600">
                  ♀ {lang === 'en' ? 'Female' : 'Pihak Wanita'}
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input placeholder={lang === 'en' ? 'Full Name *' : 'Nama Lengkap *'} value={f.female_full_name} onChange={e => set('female_full_name', e.target.value)} data-testid="ticket-match-female-name" />
                  <Input placeholder={lang === 'en' ? 'Age *' : 'Usia *'} value={f.female_age} onChange={e => set('female_age', e.target.value)} data-testid="ticket-match-female-age" />
                  <Input placeholder={lang === 'en' ? 'Domicile (city) *' : 'Domisili (kota) *'} value={f.female_domicile} onChange={e => set('female_domicile', e.target.value)} data-testid="ticket-match-female-domicile" />
                  <Input placeholder={lang === 'en' ? 'Member Code (e.g. P001) *' : 'Nomor Member (mis. P001) *'} value={f.female_member_code} onChange={e => set('female_member_code', e.target.value.toUpperCase())} data-testid="ticket-match-female-code" />
                </div>
              </div>

              {/* Confirmation checkbox */}
              <label className="flex items-start gap-2 text-xs t-text-secondary cursor-pointer">
                <input type="checkbox" checked={f.match_confirmed} onChange={e => set('match_confirmed', e.target.checked)} data-testid="ticket-match-confirm" className="mt-0.5" />
                <span>
                  {lang === 'en'
                    ? 'I confirm both sides have agreed to match. Both records will be REMOVED from the catalog drive and STAMPED "MATCH" on the website. I understand this is irreversible.'
                    : 'Saya menyatakan kedua belah pihak sudah yakin match. Data kedua anggota akan DIHAPUS dari drive/katalog dan pada kartu profil di website akan distampel "MATCH". Saya paham tindakan ini tidak dapat dibatalkan.'}
                </span>
              </label>
            </>
          )}

          {f.ticket_type === 'report' && (
            <>
              <div className="rounded-lg p-3 text-xs t-text-secondary" style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.25)' }}>
                <b className="t-text">{lang === 'en' ? 'Report a member.' : 'Laporkan oknum member.'}</b>{' '}
                {lang === 'en'
                  ? 'Please fill in the accused member details, describe what happened, and (for harassment) upload evidence. False reports may lead to your own account being sanctioned.'
                  : 'Isi data oknum member, jelaskan kejadiannya, dan (untuk pelecehan) upload bukti. Laporan palsu dapat berujung sanksi pada akun Anda sendiri.'}
              </div>

              {/* Category */}
              <div>
                <label className="text-xs t-text-muted uppercase tracking-wide mb-2 block">
                  {lang === 'en' ? 'Report Category' : 'Kategori Laporan'}
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[
                    { k: 'harassment', l: lang === 'en' ? 'Harassment (evidence required)' : 'Pelecehan (bukti wajib)' },
                    { k: 'fraud', l: lang === 'en' ? 'Fraud / Scam' : 'Penipuan' },
                    { k: 'fake_profile', l: lang === 'en' ? 'Fake Profile' : 'Profil Palsu' },
                    { k: 'inappropriate_content', l: lang === 'en' ? 'Inappropriate Content' : 'Konten Tidak Pantas' },
                    { k: 'other', l: lang === 'en' ? 'Other' : 'Lainnya' },
                  ].map(o => (
                    <button key={o.k} onClick={() => set('report_category', o.k)} data-testid={`ticket-report-cat-${o.k}`}
                      className={`p-3 rounded-lg text-left text-xs font-semibold ${f.report_category === o.k ? 'bg-[#D98A94] text-white' : 'glass-card t-text-secondary'}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Offender identity */}
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder={lang === 'en' ? 'Offender Full Name *' : 'Nama Lengkap Oknum *'}
                  value={f.offender_name} onChange={e => set('offender_name', e.target.value)}
                  data-testid="ticket-offender-name" />
                <Input placeholder={lang === 'en' ? 'Offender Code (e.g. L001 / P001) *' : 'Kode Oknum (mis. L001 / P001) *'}
                  value={f.offender_code} onChange={e => set('offender_code', e.target.value.toUpperCase())}
                  data-testid="ticket-offender-code" />
              </div>

              {/* Explanation */}
              <Textarea placeholder={lang === 'en' ? 'Explain what happened, when, and where — as detailed as possible *' : 'Jelaskan kejadiannya, kapan, dan di mana — sedetail mungkin *'}
                value={f.reason} onChange={e => set('reason', e.target.value)} className="min-h-[110px]"
                data-testid="ticket-report-reason" />

              {/* Evidence upload */}
              <div className="rounded-lg p-4 space-y-2" style={{ background: 'rgba(217,138,148,0.06)', border: '1px solid rgba(217,138,148,0.25)' }}>
                <label className="text-xs t-text-muted uppercase tracking-wide">
                  {lang === 'en' ? 'Evidence (image / PDF, max 5 files, 8 MB each)' : 'Bukti (gambar / PDF, max 5 file, 8 MB per file)'}
                  {f.report_category === 'harassment' && <span className="text-red-500"> *</span>}
                </label>
                <input type="file" accept="image/*,application/pdf" multiple
                  onChange={e => {
                    const picked = Array.from(e.target.files || []);
                    setEvidenceFiles(prev => [...prev, ...picked].slice(0, 5));
                    e.target.value = '';
                  }}
                  data-testid="ticket-evidence-input"
                  className="block text-sm t-text-secondary" />
                {evidenceFiles.length > 0 && (
                  <ul className="space-y-1 mt-2" data-testid="ticket-evidence-list">
                    {evidenceFiles.map((ef, i) => (
                      <li key={ef.name + ef.size} className="flex items-center justify-between gap-2 text-xs t-text bg-white/70 rounded px-2 py-1">
                        <span className="flex items-center gap-1 truncate">
                          <Upload className="w-3 h-3 text-green-600 flex-shrink-0" />
                          <span className="truncate">{ef.name}</span>
                          <span className="t-text-muted ml-1">({(ef.size / 1024).toFixed(0)} KB)</span>
                        </span>
                        <button
                          onClick={() => setEvidenceFiles(prev => prev.filter((_, j) => j !== i))}
                          data-testid={`ticket-evidence-remove-${i}`}
                          className="text-red-500 hover:text-red-700 text-xs font-bold"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Truth-declaration checkbox */}
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border" style={{ borderColor: f.truth_declared ? '#16A34A' : 'var(--border-color)', background: f.truth_declared ? 'rgba(22,163,74,0.06)' : 'transparent' }}>
                <input
                  type="checkbox"
                  checked={f.truth_declared}
                  onChange={e => set('truth_declared', e.target.checked)}
                  data-testid="ticket-truth-declared"
                  className="mt-0.5 w-4 h-4 accent-[#16A34A]"
                />
                <span className="text-xs t-text-secondary leading-relaxed">
                  <b className="t-text">
                    {lang === 'en'
                      ? 'I declare this report is true and accurate.'
                      : 'Saya menyatakan laporan ini benar dan akurat.'}
                  </b>{' '}
                  {lang === 'en'
                    ? 'I understand that false or malicious reports may result in sanctions against my own account under the Terms and Conditions.'
                    : 'Saya paham laporan palsu atau menyesatkan dapat berujung sanksi pada akun saya sendiri sesuai Syarat & Ketentuan.'}
                </span>
              </label>
            </>
          )}

          <Button onClick={submit} disabled={loading} data-testid="ticket-submit-btn"
            className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full h-11 font-semibold">
            {loading ? '...' : (lang === 'en' ? 'Submit Ticket' : 'Kirim Tiket')}
          </Button>
        </div>
        )}
      </div>
    </section>
  );
}
